/* eslint-disable max-len */
/**
 * Makes a search request using the TomTom
 * [Search API - Fuzzy Search](FUZZY_SEARCH_URL).
 *
 * Parameters need to be be passed to the constructor.
 *
 *
 * The list of all supported category codes are available
 * [here](SEARCH_API_SUPPORTED_CATEGORY_CODES_URL).
 *
 * ### Response
 * The response is extended with `getTrackingId()` method, which returns the `Tracking-ID`
 * associated with the request.
 *
 * Additionally, the response extends API response by providing `toGeoJson()` method, which converts fuzzy search data into
 * FeatureCollection with <a target="_blank" rel=”noopener” href="https://tools.ietf.org/html/rfc7946#section-3.1.2">Point</a> geometry.
 *
 * Each point feature represents single result from the original response. Properties of the result are mapped into feature properties
 *
 * Please refer to {{#crossLinkModule "Services"}}Difference between
 * API responses and this library's responses{{/crossLinkModule}} section.
 *
 * @example
 * ```js
 * function callbackFn(result) {
 *   console.log(result);
 * };
 *
 * tt.services.fuzzySearch({
 *   key: <Your API key>,
 *   query: 'Amsterdam'
 * }).then(callbackFn);
 * ```
 *
 * @class fuzzySearch
 * @namespace Services.services
 * @extends Search
 * @uses ExtendedPostalCodesForMixin
 * @uses CenterMixin
 * @uses RadiusMixin
 * @uses BoundingBoxMixin
 * @uses CountrySetMixin
 * @uses OffsetMixin
 * @uses BestResultMixin
 * @uses QueryMixin
 * @uses TypeaheadMixin
 * @uses MaxFuzzyLevel
 * @uses MinFuzzyLevel
 * @uses IdxSetMixin
 * @uses EntityTypeSetMixin
 * @uses CategorySetMixin
 * @uses ViewMixin
 * @uses BrandSetMixin
 * @uses ConnectorSetMixin
 * @uses OpeningHoursMixin
 * @uses MapcodesMixin
 * @uses FuelSetMixin
 * @uses MinPowerKwMixin
 * @uses MaxPowerKwMixin
 * @uses AbortSignalMixin
 * @constructor
 * @module Services
 *
 * @param {Object} [options] Options to be passed to the search call,
 *     or an array of such options objects to perform a batch request.
 * @param {Object} [additionalOptions] Additional options to be passed to the service.
 */

import {
    string as validateString, clientTime as validateClientTime, entityType as validateEntityType,
    fuzzinessLevel as validateFuzzinessLevel
} from '../validators';
import {commaSeparated as convertCommaSeparated, array as convertArray} from '../converters';
import { Search } from './search';
import { boundingBox } from './searchParameters';

class FuzzySearch extends Search {
    constructor(options, additionalOptions) {
        super(options, additionalOptions);

        this.fields.minFuzzyLevel = {
            validators: [validateFuzzinessLevel],
            defaultValue: 1
        };

        this.fields.maxFuzzyLevel = {
            validators: [validateFuzzinessLevel],
            defaultValue: 2
        };

        this.fields.idxSet = {
            validators: [validateString]
        };

        this.fields.entityTypeSet = {
            converters: [convertArray, convertCommaSeparated],
            validators: [validateEntityType]
        };

        this.fields.sessionId = {
            validators: [validateString]
        };

        this.fields.clientTime = {
            validators: [validateClientTime]
        };

        this.fields.boundingBox = boundingBox();
    }
}

export function fuzzySearch(options, additionalOptions) {
    return (new FuzzySearch(options, additionalOptions)).construct('fuzzySearch');
}
